// Layout.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo, createContext } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { useAuth } from '../context/AuthContext';
import { NotificationPanel } from './NotificationPanel';
import { LicensePermissionModal } from './LicensePermissionModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create Search Context
export const SearchContext = createContext();

export const useSearch = () => {
  const context = React.useContext(SearchContext);
  if (!context) throw new Error('useSearch must be used within SearchProvider');
  return context;
};

// ============ DESIGN TOKENS ============
const tokens = {
  colors: {
    primary: {
      50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd',
      300: '#7dd3fc', 400: '#38bdf8', 500: '#0ea5e9',
      600: '#0284c7', 700: '#0369a1', 800: '#075985', 900: '#0c4a6e',
    },
    neutral: {
      0: '#ffffff', 50: '#f8fafc', 100: '#f1f5f9',
      200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8',
      500: '#64748b', 600: '#475569', 700: '#334155',
      800: '#1e293b', 900: '#0f172a', 950: '#020617',
    },
    success: { light: '#ecfdf5', main: '#10b981', dark: '#059669' },
    warning: { light: '#fffbeb', main: '#f59e0b', dark: '#d97706' },
    error: { light: '#fef2f2', main: '#ef4444', dark: '#dc2626' },
    info: { light: '#eff6ff', main: '#3b82f6', dark: '#2563eb' },
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.15), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    top: '0 -4px 20px rgb(0 0 0 / 0.08)',
  },
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    bounce: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  radius: {
    sm: '6px', md: '8px', lg: '12px',
    xl: '16px', '2xl': '24px', full: '9999px',
  },
  spacing: {
    xs: '4px', sm: '8px', md: '12px', lg: '16px',
    xl: '20px', '2xl': '24px', '3xl': '32px',
  },
};

const MOBILE_BP = '768px';
const TABLET_BP = '1024px';

const SEARCHABLE_ROUTES = new Set(['/dashboard', '/results', '/clients', '/flagged-follow-ups']);
const QUICK_ACTIONS = [
  { label: 'Go to Dashboard', route: '/dashboard', keywords: ['dashboard', 'home', 'overview'] },
  { label: 'Open Results', route: '/results', keywords: ['result', 'results', 'report', 'score'] },
  { label: 'Open Follow Ups', route: '/flagged-follow-ups', keywords: ['flagged', 'follow-up', 'follow up', 'risk'] },
  { label: 'Open Clients', route: '/clients', keywords: ['client', 'clients', 'patient', 'email'] },
  { label: 'Send Test', route: '/send-test', keywords: ['send', 'invite', 'assessment', 'test'] },
  { label: 'View Earnings', route: '/earnings', keywords: ['earning', 'earnings', 'revenue', 'payout'] },
  { label: 'Open Profile', route: '/profile', keywords: ['profile', 'account', 'settings'] },
  { label: 'Change Password', route: '/change-password', keywords: ['password', 'reset', 'security'] },
];

const SAVED_SEARCHES = [
  { label: 'Completed this week', query: 'completed', route: '/results' },
  { label: 'Client email lookup', query: '@', route: '/clients' },
  { label: 'Recent activity', query: 'today', route: '/dashboard' },
];

// ============ ANIMATIONS ============
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(100%); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100%); }
  to { opacity: 1; transform: translateX(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92) translateY(8px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
`;

const tabSelect = keyframes`
  from { transform: translateY(4px); opacity: 0.5; }
  to { transform: translateY(0); opacity: 1; }
`;

// ============ ICON COMPONENTS ============
const Icons = {
  Menu: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  Close: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="15,18 9,12 15,6" />
    </svg>
  ),
  ChevronRight: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polyline points="9,18 15,12 9,6" />
    </svg>
  ),
  Dashboard: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  Send: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
    </svg>
  ),
  Results: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  Earnings: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  Users: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Profile: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Lock: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Logout: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16,17 21,12 16,7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  Bell: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  Search: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  ),
  Settings: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  Help: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  More: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
    </svg>
  ),
  Star: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  ),
};

// ============ ROOT CONTAINER ============
const LayoutRoot = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${tokens.colors.neutral[50]};
  position: relative;

  /* Safe area support for modern mobile devices */
  padding-bottom: env(safe-area-inset-bottom);
`;

// ============ DESKTOP SIDEBAR ============
const DesktopSidebar = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${p => p.$collapsed ? '72px' : '256px'};
  background: ${tokens.colors.neutral[0]};
  border-right: 1px solid ${tokens.colors.neutral[200]};
  display: flex;
  flex-direction: column;
  z-index: 100;
  transition: width ${tokens.transitions.slow};

  @media (max-width: ${TABLET_BP}) {
    display: none;
  }
`;

// ============ MOBILE DRAWER ============
const DrawerBackdrop = styled.div`
  display: none;

  @media (max-width: ${TABLET_BP}) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    z-index: 200;
    opacity: ${p => p.$visible ? 1 : 0};
    pointer-events: ${p => p.$visible ? 'auto' : 'none'};
    transition: opacity ${tokens.transitions.normal};
  }
`;

const MobileDrawer = styled.div`
  display: none;

  @media (max-width: ${TABLET_BP}) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: min(320px, 88vw);
    background: ${tokens.colors.neutral[0]};
    z-index: 300;
    transform: translateX(${p => p.$open ? '0' : '-100%'});
    transition: transform ${tokens.transitions.slow};
    box-shadow: ${p => p.$open ? tokens.shadows.xl : 'none'};
    overflow: hidden;
  }
`;

const DrawerHandle = styled.div`
  width: 40px;
  height: 4px;
  background: ${tokens.colors.neutral[200]};
  border-radius: ${tokens.radius.full};
  margin: ${tokens.spacing.md} auto;
`;

// Shared sidebar inner content
const SidebarInner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const SidebarHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${tokens.spacing['2xl']};
  border-bottom: 1px solid ${tokens.colors.neutral[100]};
  min-height: 68px;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
`;

const LogoMark = styled.div`
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: ${tokens.radius.lg};
  overflow: hidden;
  background: linear-gradient(135deg, ${tokens.colors.primary[500]}, ${tokens.colors.primary[700]});
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const LogoLabel = styled.div`
  opacity: ${p => p.$collapsed ? 0 : 1};
  max-width: ${p => p.$collapsed ? 0 : '180px'};
  overflow: hidden;
  transition: all ${tokens.transitions.slow};
  white-space: nowrap;

  h1 {
    font-size: 0.95rem;
    font-weight: 700;
    color: ${tokens.colors.neutral[900]};
    margin: 0;
    line-height: 1.2;
  }
  span {
    font-size: 0.68rem;
    color: ${tokens.colors.neutral[400]};
    font-weight: 500;
  }
`;

const CollapseBtn = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.colors.neutral[100]};
  border: none;
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[500]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  flex-shrink: 0;

  svg {
    transition: transform ${tokens.transitions.normal};
    transform: rotate(${p => p.$collapsed ? '180deg' : '0deg'});
  }

  &:hover {
    background: ${tokens.colors.neutral[200]};
    color: ${tokens.colors.neutral[800]};
  }
`;

const DrawerCloseBtn = styled.button`
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${tokens.colors.neutral[100]};
  border: none;
  border-radius: ${tokens.radius.full};
  color: ${tokens.colors.neutral[600]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};

  &:hover {
    background: ${tokens.colors.neutral[200]};
    color: ${tokens.colors.neutral[900]};
  }
`;

// Mobile Drawer Profile Header
const DrawerProfile = styled.div`
  padding: ${tokens.spacing.xl} ${tokens.spacing['2xl']};
  background: linear-gradient(135deg, ${tokens.colors.primary[600]} 0%, ${tokens.colors.primary[800]} 100%);
  color: white;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
`;

const DrawerAvatar = styled.div`
  width: 52px;
  height: 52px;
  background: rgba(255, 255, 255, 0.25);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: ${tokens.radius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
  backdrop-filter: blur(4px);
`;

const DrawerUserInfo = styled.div`
  .name {
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.3;
  }
  .role {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 2px;
  }
  .status {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.7rem;
    opacity: 0.9;
    margin-top: 4px;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      background: #4ade80;
      border-radius: 50%;
      box-shadow: 0 0 0 2px rgba(74, 222, 128, 0.3);
    }
  }
`;

const SidebarScrollable = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${tokens.spacing.lg} ${tokens.spacing.md};

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb {
    background: ${tokens.colors.neutral[200]};
    border-radius: 2px;
  }
`;

const NavSectionLabel = styled.p`
  font-size: 0.62rem;
  font-weight: 700;
  color: ${tokens.colors.neutral[400]};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0 ${tokens.spacing.md};
  margin: 0 0 ${tokens.spacing.sm} 0;
  opacity: ${p => p.$collapsed ? 0 : 1};
  height: ${p => p.$collapsed ? 0 : 'auto'};
  overflow: hidden;
  transition: all ${tokens.transitions.slow};
`;

const NavSection = styled.div`
  margin-bottom: ${tokens.spacing['2xl']};
`;

// Custom wrapper to prevent emotion from passing $ props to DOM
const NavLinkWrapper = React.forwardRef(({ $collapsed, ...props }, ref) => (
  <NavLink ref={ref} {...props} />
));
NavLinkWrapper.displayName = 'NavLinkWrapper';

const SideNavItem = styled(NavLinkWrapper)`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
  padding: 10px ${tokens.spacing.md};
  margin: 2px 0;
  border-radius: ${tokens.radius.lg};
  color: ${tokens.colors.neutral[600]};
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all ${tokens.transitions.fast};
  position: relative;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  .icon {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${tokens.colors.neutral[400]};
    transition: color ${tokens.transitions.fast};
  }

  .label {
    opacity: ${p => p.$collapsed ? 0 : 1};
    max-width: ${p => p.$collapsed ? 0 : '160px'};
    overflow: hidden;
    white-space: nowrap;
    transition: all ${tokens.transitions.slow};
  }

  .badge {
    margin-left: auto;
    background: ${tokens.colors.error.main};
    color: white;
    font-size: 0.6rem;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: ${tokens.radius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${p => p.$collapsed ? 0 : 1};
    transition: opacity ${tokens.transitions.slow};
  }

  &:hover {
    background: ${tokens.colors.neutral[100]};
    color: ${tokens.colors.neutral[900]};
    .icon { color: ${tokens.colors.neutral[700]}; }
  }

  &.active {
    background: ${tokens.colors.primary[50]};
    color: ${tokens.colors.primary[700]};
    font-weight: 600;

    .icon { color: ${tokens.colors.primary[600]}; }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 22px;
      background: ${tokens.colors.primary[500]};
      border-radius: 0 3px 3px 0;
    }
  }

  ${p => p.$collapsed && css`
    &::after {
      content: attr(data-tip);
      position: absolute;
      left: calc(100% + 10px);
      padding: 6px 10px;
      background: ${tokens.colors.neutral[800]};
      color: white;
      font-size: 0.75rem;
      font-weight: 500;
      border-radius: ${tokens.radius.md};
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity ${tokens.transitions.fast};
      z-index: 999;
      box-shadow: ${tokens.shadows.lg};
    }
    &:hover::after { opacity: 1; }
  `}
`;

const SidebarFoot = styled.div`
  padding: ${tokens.spacing.lg};
  border-top: 1px solid ${tokens.colors.neutral[100]};
`;

const UserChip = styled.button`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
  padding: ${tokens.spacing.md};
  border-radius: ${tokens.radius.lg};
  background: ${tokens.colors.neutral[50]};
  border: none;
  margin-bottom: ${tokens.spacing.sm};
  width: 100%;
  cursor: pointer;
  text-align: left;
  transition: all ${tokens.transitions.fast};
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};


  &:hover, &:focus-visible {
    background: ${tokens.colors.primary[50]};
    outline: none;
    box-shadow: 0 0 0 3px ${tokens.colors.primary[100]};
  }
`;

const SmallAvatar = styled.div`
  width: 34px;
  height: 34px;
  background: linear-gradient(135deg, ${tokens.colors.primary[400]} 0%, ${tokens.colors.primary[600]} 100%);
  border-radius: ${tokens.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 0.75rem;
  flex-shrink: 0;
`;

const UserChipInfo = styled.div`
  opacity: ${p => p.$collapsed ? 0 : 1};
  max-width: ${p => p.$collapsed ? 0 : '140px'};
  overflow: hidden;
  transition: all ${tokens.transitions.slow};

  .name {
    font-size: 0.82rem;
    font-weight: 600;
    color: ${tokens.colors.neutral[800]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .role {
    font-size: 0.68rem;
    color: ${tokens.colors.neutral[500]};
  }
`;

const SignOutBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  gap: ${tokens.spacing.md};
  padding: ${tokens.spacing.md};
  background: transparent;
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[600]};
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  touch-action: manipulation;

  .text {
    opacity: ${p => p.$collapsed ? 0 : 1};
    max-width: ${p => p.$collapsed ? 0 : '120px'};
    overflow: hidden;
    white-space: nowrap;
    transition: all ${tokens.transitions.slow};
  }

  &:hover, &:active {
    background: ${tokens.colors.error.light};
    border-color: ${tokens.colors.error.main};
    color: ${tokens.colors.error.dark};
  }
`;

// Drawer full-width nav item
const DrawerNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.lg};
  padding: 14px ${tokens.spacing.xl};
  margin: 2px 0;
  border-radius: ${tokens.radius.lg};
  color: ${tokens.colors.neutral[600]};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all ${tokens.transitions.fast};
  position: relative;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  .icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${tokens.colors.neutral[400]};
    transition: all ${tokens.transitions.fast};
  }

  .label { flex: 1; }

  .badge {
    background: ${tokens.colors.error.main};
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: ${tokens.radius.full};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .arrow {
    color: ${tokens.colors.neutral[300]};
    margin-left: auto;
  }

  &:hover {
    background: ${tokens.colors.neutral[100]};
    color: ${tokens.colors.neutral[900]};
    .icon { color: ${tokens.colors.neutral[700]}; }
  }

  &.active {
    background: ${tokens.colors.primary[50]};
    color: ${tokens.colors.primary[700]};
    font-weight: 600;

    .icon { color: ${tokens.colors.primary[600]}; }
    .arrow { color: ${tokens.colors.primary[400]}; }
  }
`;

const DrawerSection = styled.div`
  padding: 0 ${tokens.spacing.md};
  margin-bottom: ${tokens.spacing['2xl']};
`;

const DrawerSectionLabel = styled.p`
  font-size: 0.62rem;
  font-weight: 700;
  color: ${tokens.colors.neutral[400]};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0 ${tokens.spacing.md};
  margin: 0 0 ${tokens.spacing.sm} 0;
`;

const DrawerFoot = styled.div`
  padding: ${tokens.spacing.xl};
  border-top: 1px solid ${tokens.colors.neutral[100]};
  margin-top: auto;
`;

const DrawerSignOutBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${tokens.spacing.md};
  padding: 14px;
  background: ${tokens.colors.error.light};
  border: 1.5px solid ${tokens.colors.error.main}22;
  border-radius: ${tokens.radius.lg};
  color: ${tokens.colors.error.dark};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  touch-action: manipulation;

  &:hover, &:active {
    background: #fee2e2;
    border-color: ${tokens.colors.error.main};
  }
`;

// ============ MAIN CONTENT ============
const MainArea = styled.main`
  flex: 1;
  margin-left: ${p => p.$collapsed ? '72px' : '256px'};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: margin-left ${tokens.transitions.slow};

  @media (max-width: ${TABLET_BP}) {
    margin-left: 0;
    /* Push content above bottom nav */
    padding-bottom: calc(64px + env(safe-area-inset-bottom));
  }
`;

// ============ TOP BAR ============
const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 ${tokens.spacing['2xl']};
  gap: ${tokens.spacing.lg};

  @media (max-width: ${MOBILE_BP}) {
    padding: 0 ${tokens.spacing.lg};
    height: 56px;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
  flex: 1;
  min-width: 0;
`;

const MobileMenuBtn = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  background: ${tokens.colors.neutral[100]};
  border: none;
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[700]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  flex-shrink: 0;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:active { background: ${tokens.colors.neutral[200]}; }

  @media (max-width: ${TABLET_BP}) { display: flex; }
`;

const PageTitle = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BP}) {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    color: ${tokens.colors.neutral[900]};
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
  font-size: 0.83rem;

  @media (max-width: ${MOBILE_BP}) { display: none; }

  span { color: ${tokens.colors.neutral[400]}; }

  a {
    color: ${tokens.colors.neutral[500]};
    text-decoration: none;
    transition: color ${tokens.transitions.fast};
    &:hover { color: ${tokens.colors.primary[600]}; }
  }

  .current {
    color: ${tokens.colors.neutral[800]};
    font-weight: 600;
  }
`;

const SearchBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
  background: ${tokens.colors.neutral[100]};
  border: 1.5px solid transparent;
  border-radius: ${tokens.radius.lg};
  padding: ${tokens.spacing.sm} ${tokens.spacing.md};
  width: 260px;
  transition: all ${tokens.transitions.fast};
  flex-shrink: 0;

  &:focus-within {
    background: ${tokens.colors.neutral[0]};
    border-color: ${tokens.colors.primary[300]};
    box-shadow: 0 0 0 3px ${tokens.colors.primary[100]};
    width: 300px;
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 0.875rem;
    color: ${tokens.colors.neutral[800]};
    outline: none;
    min-width: 0;

    &::placeholder { color: ${tokens.colors.neutral[400]};  }
  }

  .search-trigger {
    width: 30px;
    height: 30px;
    border: none;
    border-radius: ${tokens.radius.sm};
    background: transparent;
    color: ${tokens.colors.neutral[400]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: ${tokens.colors.neutral[100]};
      color: ${tokens.colors.neutral[700]};
    }
  }

  @media (max-width: ${MOBILE_BP}) { display: none; }
`;

const SearchMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: min(480px, 100%);
  background: ${tokens.colors.neutral[0]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadows.lg};
  z-index: 220;
  padding: ${tokens.spacing.sm};
`;

const SearchMenuSection = styled.div`
  & + & {
    margin-top: ${tokens.spacing.sm};
    padding-top: ${tokens.spacing.sm};
    border-top: 1px solid ${tokens.colors.neutral[100]};
  }

  .title {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: ${tokens.colors.neutral[500]};
    text-transform: uppercase;
    margin: 0 0 6px;
    padding: 0 6px;
  }
`;

const SearchMenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  border-radius: ${tokens.radius.md};
  padding: 8px 10px;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  color: ${tokens.colors.neutral[700]};
  text-align: left;

  &:hover { background: ${tokens.colors.neutral[100]}; }

  .hint {
    font-size: 0.75rem;
    color: ${tokens.colors.neutral[500]};
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: ${tokens.radius.md};
  color: ${tokens.colors.neutral[500]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  position: relative;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover { background: ${tokens.colors.neutral[100]}; color: ${tokens.colors.neutral[700]}; }
  &:active { background: ${tokens.colors.neutral[200]}; }

  .dot {
    position: absolute;
    top: 6px;
    right: 6px;
    min-width: 16px;
    height: 16px;
    background: ${tokens.colors.error.main};
    border-radius: 50%;
    border: 2px solid white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.58rem;
    font-weight: 800;
    color: white;
    animation: ${pulse} 2s ease-in-out infinite;
  }

  @media (max-width: ${MOBILE_BP}) {
    &.desktop-only { display: none; }
  }

  @media (min-width: calc(${MOBILE_BP} + 1px)) {
    &.mobile-only { display: none; }
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: ${tokens.colors.neutral[200]};
  margin: 0 4px;

  @media (max-width: ${MOBILE_BP}) { display: none; }
`;

const TopBarUserPill = styled.button`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
  padding: 4px 8px 4px 4px;
  border-radius: ${tokens.radius.full};
  background: transparent;
  border: 1.5px solid ${tokens.colors.neutral[200]};
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  touch-action: manipulation;

  &:hover {
    background: ${tokens.colors.neutral[50]};
    border-color: ${tokens.colors.neutral[300]};
  }

  @media (max-width: ${MOBILE_BP}) { display: none; }
`;

const MiniAvatar = styled.div`
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, ${tokens.colors.primary[400]}, ${tokens.colors.primary[600]});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 0.65rem;
  flex-shrink: 0;
`;

const MiniUserInfo = styled.div`
  .name {
    font-size: 0.78rem;
    font-weight: 600;
    color: ${tokens.colors.neutral[800]};
    line-height: 1;
  }
  .online {
    font-size: 0.62rem;
    color: ${tokens.colors.success.main};
    margin-top: 2px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
`;

// ============ MOBILE SEARCH OVERLAY ============
const MobileSearchOverlay = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BP}) {
    display: ${p => p.$open ? 'flex' : 'none'};
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(8px);
    z-index: 400;
    flex-direction: column;
    animation: ${fadeIn} 150ms ease;
    padding: env(safe-area-inset-top, 0) 0 0;
  }
`;

const MobileSearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.md};
  padding: ${tokens.spacing.lg};
  border-bottom: 1px solid ${tokens.colors.neutral[200]};
`;

const MobileSearchInput = styled.input`
  flex: 1;
  border: none;
  background: ${tokens.colors.neutral[100]};
  border-radius: ${tokens.radius.lg};
  padding: 12px ${tokens.spacing.lg};
  font-size: 1rem;
  color: ${tokens.colors.neutral[800]};
  outline: none;

  &::placeholder { color: ${tokens.colors.neutral[400]}; }
`;

const MobileSearchCancel = styled.button`
  background: none;
  border: none;
  color: ${tokens.colors.primary[600]};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: ${tokens.spacing.sm};
  flex-shrink: 0;
`;

const MobileSearchHint = styled.div`
  padding: ${tokens.spacing['2xl']};
  text-align: center;
  color: ${tokens.colors.neutral[400]};
  font-size: 0.875rem;
`;

// ============ BOTTOM NAVIGATION BAR (Mobile Only) ============
const BottomNav = styled.nav`
  display: none;

  @media (max-width: ${TABLET_BP}) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(64px + env(safe-area-inset-bottom));
    padding-bottom: env(safe-area-inset-bottom);
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-top: 1px solid ${tokens.colors.neutral[200]};
    box-shadow: ${tokens.shadows.top};
    z-index: 150;
    align-items: stretch;
  }
`;

const BottomNavItem = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  text-decoration: none;
  color: ${tokens.colors.neutral[400]};
  font-size: 0.6rem;
  font-weight: 500;
  transition: all ${tokens.transitions.fast};
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  min-width: 0;

  .icon-wrap {
    position: relative;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 56px;
  }

  .badge {
    position: absolute;
    top: -4px;
    right: -6px;
    min-width: 15px;
    height: 15px;
    background: ${tokens.colors.error.main};
    border-radius: ${tokens.radius.full};
    border: 1.5px solid white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5rem;
    font-weight: 800;
    color: white;
  }

  &.active {
    color: ${tokens.colors.primary[600]};
    animation: ${tabSelect} ${tokens.transitions.spring};

    .icon-wrap::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 4px;
      height: 4px;
      background: ${tokens.colors.primary[500]};
      border-radius: 50%;
    }
  }

  &:active {
    transform: scale(0.9);
  }
`;

const BottomNavMore = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  color: ${tokens.colors.neutral[400]};
  font-size: 0.6rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.transitions.fast};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  padding: 0;

  .icon-wrap {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:active { transform: scale(0.9); color: ${tokens.colors.primary[600]}; }
`;

// ============ MOBILE MORE MENU (sheet) ============
const MoreSheet = styled.div`
  display: none;

  @media (max-width: ${TABLET_BP}) {
    display: block;
    position: fixed;
    bottom: ${p => p.$open ? '0' : '-100%'};
    left: 0;
    right: 0;
    background: ${tokens.colors.neutral[0]};
    border-radius: ${tokens.radius['2xl']} ${tokens.radius['2xl']} 0 0;
    box-shadow: ${tokens.shadows.xl};
    z-index: 350;
    transition: bottom ${tokens.transitions.slow};
    padding-bottom: env(safe-area-inset-bottom);
    max-height: 70vh;
    overflow-y: auto;
  }
`;

const MoreSheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: ${tokens.colors.neutral[200]};
  border-radius: ${tokens.radius.full};
  margin: ${tokens.spacing.md} auto;
`;

const MoreSheetItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.lg};
  padding: 16px ${tokens.spacing.xl};
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  color: ${tokens.colors.neutral[700]};
  font-size: 0.95rem;
  font-weight: 500;
  transition: background ${tokens.transitions.fast};
  touch-action: manipulation;

  .icon {
    width: 40px;
    height: 40px;
    background: ${tokens.colors.neutral[100]};
    border-radius: ${tokens.radius.lg};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${tokens.colors.neutral[600]};
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    .title { font-size: 0.9rem; font-weight: 600; }
    .desc { font-size: 0.75rem; color: ${tokens.colors.neutral[500]}; margin-top: 2px; }
  }

  &:active { background: ${tokens.colors.neutral[50]}; }
`;

const MoreSheetDanger = styled(MoreSheetItem)`
  color: ${tokens.colors.error.dark};

  .icon {
    background: ${tokens.colors.error.light};
    color: ${tokens.colors.error.main};
  }
`;

const MoreSheetDivider = styled.div`
  height: 1px;
  background: ${tokens.colors.neutral[100]};
  margin: 0 ${tokens.spacing.xl};
`;

const MoreSheetBackdrop = styled.div`
  display: none;

  @media (max-width: ${TABLET_BP}) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.35);
    backdrop-filter: blur(4px);
    z-index: 340;
    opacity: ${p => p.$visible ? 1 : 0};
    pointer-events: ${p => p.$visible ? 'auto' : 'none'};
    transition: opacity ${tokens.transitions.normal};
  }
`;

// ============ MAIN LAYOUT COMPONENT ============
const Layout = ({ children }) => {
  const { user, logout, isApproved } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const knownResultIdsRef = useRef(new Set());
  const hasInitializedResultsRef = useRef(false);

  const mobileSearchRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const searchMenuRef = useRef(null);

  const smartSearchRoute = useCallback((query) => {
    const q = query.toLowerCase();
    const actionMatch = QUICK_ACTIONS.find(action =>
      action.keywords.some(keyword => q.includes(keyword))
    );
    return actionMatch?.route || '/results';
  }, []);

  const searchActionItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return QUICK_ACTIONS.slice(0, 4);
    return QUICK_ACTIONS.filter(action =>
      action.label.toLowerCase().includes(q) ||
      action.keywords.some(keyword => keyword.includes(q))
    ).slice(0, 5);
  }, [searchQuery]);

  const handleSearchSubmit = useCallback((forcedRoute, queryOverride) => {
    const trimmed = (queryOverride ?? searchQuery).trim();
    if (!trimmed && !forcedRoute) return;

    const targetRoute = forcedRoute || (
      SEARCHABLE_ROUTES.has(location.pathname) ? location.pathname : smartSearchRoute(trimmed)
    );
    const routeWithQuery = trimmed
      ? `${targetRoute}?q=${encodeURIComponent(trimmed)}`
      : targetRoute;

    setIsSearchMenuOpen(false);
    setIsMobileSearchOpen(false);
    if (location.pathname !== targetRoute || location.search !== (trimmed ? `?q=${encodeURIComponent(trimmed)}` : '')) {
      navigate(routeWithQuery);
    }
  }, [searchQuery, location.pathname, location.search, navigate, smartSearchRoute]);

  // ─── Notification helper ───────────────────────────
  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [{
      id,
      timestamp: new Date(),
      isRead: false,
      ...notification,
    }, ...prev]);
    return id;
  }, []);

  // ─── License expiry check ──────────────────────────
  useEffect(() => {
    if (!user?.licenseExpiry) return;
    const days = Math.ceil(
      (new Date(user.licenseExpiry) - new Date()) / (1000 * 60 * 60 * 24)
    );
    if (days > 0 && days <= 30) {
      addNotification({
        type: 'warning',
        title: 'License Expiring Soon',
        message: `Your license expires in ${days} day${days !== 1 ? 's' : ''}.`,
        actions: [{ label: 'Request Update', variant: 'primary', onClick: () => setShowLicenseModal(true) }],
      });
    } else if (days <= 0) {
      addNotification({
        type: 'error',
        title: 'License Expired',
        message: 'Your professional license has expired. Please renew immediately.',
        actions: [{ label: 'Update Status', variant: 'primary', onClick: () => setShowLicenseModal(true) }],
      });
    }
  }, [user?.licenseExpiry, addNotification]);

  // ─── Storage event (cross-tab form completion) ─────
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'formCompleted') return;
      const data = JSON.parse(e.newValue || '{}');
      if (data.practitionerId !== user?.id) return;
      addNotification({
        type: 'success',
        title: 'Client Form Completed',
        message: `${data.clientName} completed the assessment.`,
        actions: [{ label: 'View Results', variant: 'primary', onClick: () => navigate('/results') }],
      });
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [user?.id, navigate, addNotification]);

  // ─── Poll for newly completed client tests ─────────
  useEffect(() => {
    if (!user?.id || !isApproved) return;

    const pollCompletedResults = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const res = await fetch(`${API_URL}/practitioner/results?page=1&limit=25`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) return;

        const data = await res.json();
        const latestResults = Array.isArray(data?.results) ? data.results : [];

        const incomingIds = latestResults
          .map(result => String(result.id))
          .filter(Boolean);

        if (!hasInitializedResultsRef.current) {
          knownResultIdsRef.current = new Set(incomingIds);
          hasInitializedResultsRef.current = true;
          return;
        }

        const newResults = latestResults.filter(
          result => result?.id && !knownResultIdsRef.current.has(String(result.id))
        );

        newResults.forEach((result) => {
          const clientName = `${result?.Client?.firstName || ''} ${result?.Client?.lastName || ''}`.trim() || 'A client';
          const testName = result?.TestCatalog?.shortName || result?.TestCatalog?.name || 'assessment';

          addNotification({
            type: 'success',
            category: 'result',
            resultId: result.id,
            title: 'New Test Result',
            message: `${clientName} completed ${testName}.`,
            actions: [{ label: 'View Results', variant: 'primary', onClick: () => navigate('/results') }],
          });
        });

        knownResultIdsRef.current = new Set([
          ...knownResultIdsRef.current,
          ...incomingIds,
        ]);
      } catch (error) {
        console.error('Failed to poll completed results:', error);
      }
    };

    pollCompletedResults();
    const intervalId = window.setInterval(pollCompletedResults, 30000);
    return () => window.clearInterval(intervalId);
  }, [user?.id, isApproved, addNotification, navigate]);

  // ─── License renewal submit ────────────────────────
  const handleLicensePermissionSubmit = useCallback(async (reason) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/tests/license-renewal-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ practitionerId: user?.id, reason, licenseExpiry: user?.licenseExpiry }),
      });
      if (res.ok) {
        addNotification({ type: 'success', title: 'Request Submitted', message: 'Your renewal request has been submitted.' });
        setShowLicenseModal(false);
      } else {
        addNotification({ type: 'error', title: 'Submission Failed', message: 'Please try again.' });
      }
    } catch {
      addNotification({ type: 'error', title: 'Error', message: 'An error occurred. Please try again.' });
    }
  }, [user?.id, user?.licenseExpiry, addNotification]);

  // ─── Close panels on route change ─────────────────
  useEffect(() => {
    setIsMobileDrawerOpen(false);
    setIsMoreSheetOpen(false);
    setIsMobileSearchOpen(false);
    setIsSearchMenuOpen(false);
  }, [location.pathname]);

  // ─── Keep search query in sync with URL ?q= ───────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q !== null) setSearchQuery(q);
  }, [location.search]);

  // ─── Keyboard escape ──────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsMobileDrawerOpen(false);
        setIsMoreSheetOpen(false);
        setIsMobileSearchOpen(false);
        setIsSearchMenuOpen(false);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSearchMenuOpen(true);
        desktopSearchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ─── Close search menu on outside click ───────────
  useEffect(() => {
    const onPointerDown = (e) => {
      if (!isSearchMenuOpen) return;
      if (
        searchMenuRef.current?.contains(e.target) ||
        desktopSearchRef.current?.contains(e.target)
      ) {
        return;
      }
      setIsSearchMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isSearchMenuOpen]);

  // ─── Body scroll lock ─────────────────────────────
  useEffect(() => {
    const locked = isMobileDrawerOpen || isMoreSheetOpen || isMobileSearchOpen;
    document.body.style.overflow = locked ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileDrawerOpen, isMoreSheetOpen, isMobileSearchOpen]);

  // ─── Focus mobile search input ────────────────────
  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchRef.current) {
      setTimeout(() => mobileSearchRef.current?.focus(), 100);
    }
  }, [isMobileSearchOpen]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const getPageTitle = useCallback(() => {
    const routes = {
      '/dashboard': 'Dashboard',
      '/send-test': 'Send Test',
      '/results': 'Results',
      '/flagged-follow-ups': 'Flagged Follow Ups',
      '/clients': 'Clients',
      '/earnings': 'Earnings',
      '/profile': 'Profile',
      '/change-password': 'Change Password',
    };
    return routes[location.pathname] || 'Dashboard';
  }, [location.pathname]);

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const unreadResultsCount = notifications.filter(n => !n.isRead && n.category === 'result').length;
  const userInitials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`;

  const mainNavItems = [
    { path: '/dashboard', icon: Icons.Dashboard, label: 'Dashboard' },
    ...(isApproved ? [
      { path: '/send-test', icon: Icons.Send, label: 'Send Test' },
      { path: '/results', icon: Icons.Results, label: 'Results', badge: unreadResultsCount || undefined },
      { path: '/flagged-follow-ups', icon: Icons.Results, label: 'Follow Ups' },
      { path: '/clients', icon: Icons.Users, label: 'Clients' },
      { path: '/earnings', icon: Icons.Earnings, label: 'Earnings' },
    ] : []),
  ];

  const handleRemoveNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const handleMarkNotificationAsRead = useCallback((id) => {
    setNotifications(prev => prev.map(notification => (
      notification.id === id ? { ...notification, isRead: true } : notification
    )));
  }, []);

  const handleClearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const settingsNavItems = [
    { path: '/profile', icon: Icons.Profile, label: 'My Profile' },
    { path: '/change-password', icon: Icons.Lock, label: 'Change Password' },
  ];

  // Bottom nav shows first 4 main items + "More" tab
  const bottomNavItems = mainNavItems.slice(0, 4);

  // ─── Shared sidebar content renderer ──────────────
  const renderSidebarContent = (collapsed = false, isMobileDrawer = false) => (
    <SidebarInner>
      {isMobileDrawer ? (
        <>
          {/* Mobile drawer profile header */}
          <DrawerProfile>
            <DrawerAvatar>{userInitials}</DrawerAvatar>
            <DrawerUserInfo>
              <div className="name">{user?.firstName} {user?.lastName}</div>
              <div className="role">Practitioner</div>
              <div className="status">Online</div>
            </DrawerUserInfo>
          </DrawerProfile>

          <SidebarScrollable>
            <DrawerSection>
              <DrawerSectionLabel>Main Menu</DrawerSectionLabel>
              {mainNavItems.map(item => (
                <DrawerNavItem key={item.path} to={item.path}>
                  <span className="icon"><item.icon /></span>
                  <span className="label">{item.label}</span>
                  {item.badge && <span className="badge">{item.badge}</span>}
                  <span className="arrow"><Icons.ChevronRight /></span>
                </DrawerNavItem>
              ))}
            </DrawerSection>

            <DrawerSection>
              <DrawerSectionLabel>Account</DrawerSectionLabel>
              {settingsNavItems.map(item => (
                <DrawerNavItem key={item.path} to={item.path}>
                  <span className="icon"><item.icon /></span>
                  <span className="label">{item.label}</span>
                  <span className="arrow"><Icons.ChevronRight /></span>
                </DrawerNavItem>
              ))}
              <DrawerNavItem to="/help" onClick={e => e.preventDefault()}>
                <span className="icon"><Icons.Help /></span>
                <span className="label">Help & Support</span>
                <span className="arrow"><Icons.ChevronRight /></span>
              </DrawerNavItem>
            </DrawerSection>
          </SidebarScrollable>

          <DrawerFoot>
            <DrawerSignOutBtn onClick={handleLogout}>
              <Icons.Logout />
              Sign Out
            </DrawerSignOutBtn>
          </DrawerFoot>
        </>
      ) : (
        <>
          {/* Desktop sidebar header */}
          <SidebarHead>
            <LogoRow>
              <LogoMark>
                <img src="/logo192.png" alt="Visioning" />
              </LogoMark>
              <LogoLabel $collapsed={collapsed}>
                <h1>Visioning</h1>
                <span>Assessment Portal</span>
              </LogoLabel>
            </LogoRow>
            <CollapseBtn
              $collapsed={collapsed}
              onClick={() => setIsCollapsed(c => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icons.ChevronLeft />
            </CollapseBtn>
          </SidebarHead>

          <SidebarScrollable>
            <NavSection>
              <NavSectionLabel $collapsed={collapsed}>Main Menu</NavSectionLabel>
              {mainNavItems.map(item => (
                <SideNavItem key={item.path} to={item.path} $collapsed={collapsed} data-tip={item.label}>
                  <span className="icon"><item.icon /></span>
                  <span className="label">{item.label}</span>
                  {item.badge && <span className="badge">{item.badge}</span>}
                </SideNavItem>
              ))}
            </NavSection>

            <NavSection>
              <NavSectionLabel $collapsed={collapsed}>Account</NavSectionLabel>
              {settingsNavItems.map(item => (
                <SideNavItem key={item.path} to={item.path} $collapsed={collapsed} data-tip={item.label}>
                  <span className="icon"><item.icon /></span>
                  <span className="label">{item.label}</span>
                </SideNavItem>
              ))}
            </NavSection>
          </SidebarScrollable>

          <SidebarFoot>
            <UserChip
              $collapsed={collapsed}
              type="button"
              onClick={() => navigate('/change-password')}
              aria-label="Open change password page"
              title="Change password"
            >
              <SmallAvatar>{userInitials}</SmallAvatar>
              <UserChipInfo $collapsed={collapsed}>
                <div className="name">{user?.firstName} {user?.lastName}</div>
                <div className="role">Practitioner</div>
              </UserChipInfo>
            </UserChip>
            <SignOutBtn $collapsed={collapsed} onClick={handleLogout} aria-label="Sign out">
              <Icons.Logout />
              <span className="text">Sign Out</span>
            </SignOutBtn>
          </SidebarFoot>
        </>
      )}
    </SidebarInner>
  );

  return (
    <LayoutRoot>
      {/* ── Desktop Sidebar ───────────────────────── */}
      <DesktopSidebar $collapsed={isCollapsed} role="navigation" aria-label="Main navigation">
        {renderSidebarContent(isCollapsed, false)}
      </DesktopSidebar>

      {/* ── Mobile Drawer Backdrop ────────────────── */}
      <DrawerBackdrop
        $visible={isMobileDrawerOpen}
        onClick={() => setIsMobileDrawerOpen(false)}
        aria-hidden="true"
      />

      {/* ── Mobile Drawer ─────────────────────────── */}
      <MobileDrawer $open={isMobileDrawerOpen} role="navigation" aria-label="Mobile navigation">
        <SidebarHead style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <LogoRow>
            <LogoMark>
              <img src="/logo192.png" alt="Visioning" />
            </LogoMark>
            <LogoLabel>
              <h1>Visioning</h1>
              <span>Assessment Portal</span>
            </LogoLabel>
          </LogoRow>
          <DrawerCloseBtn onClick={() => setIsMobileDrawerOpen(false)} aria-label="Close menu">
            <Icons.Close />
          </DrawerCloseBtn>
        </SidebarHead>
        {renderSidebarContent(false, true)}
      </MobileDrawer>

      {/* ── Main Content ──────────────────────────── */}
      <MainArea $collapsed={isCollapsed}>
        {/* Top Bar */}
        <TopBar>
          <TopBarLeft>
            {/* Hamburger — mobile only */}
            <MobileMenuBtn
              onClick={() => setIsMobileDrawerOpen(true)}
              aria-label="Open menu"
            >
              <Icons.Menu />
            </MobileMenuBtn>

            {/* Mobile page title */}
            <PageTitle>{getPageTitle()}</PageTitle>

            {/* Desktop breadcrumb */}
            <Breadcrumb aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span>/</span>
              <span className="current">{getPageTitle()}</span>
            </Breadcrumb>

            {/* Desktop search */}
            <SearchBar>
              <button
                className="search-trigger"
                aria-label="Run search"
                onClick={() => handleSearchSubmit()}
              >
                <Icons.Search />
              </button>
              <input
                ref={desktopSearchRef}
                type="text"
                placeholder="Search anything…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchMenuOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchSubmit();
                  if (e.key === 'ArrowDown') setIsSearchMenuOpen(true);
                }}
                aria-label="Search"
              />
              {isSearchMenuOpen && (
                <SearchMenu ref={searchMenuRef}>
                  <SearchMenuSection>
                    <p className="title">Quick actions</p>
                    {searchActionItems.map(action => (
                      <SearchMenuItem
                        key={action.route}
                        onClick={() => handleSearchSubmit(action.route)}
                      >
                        <span>{action.label}</span>
                        <span className="hint">{action.route}</span>
                      </SearchMenuItem>
                    ))}
                  </SearchMenuSection>

                  <SearchMenuSection>
                    <p className="title">Saved searches</p>
                    {SAVED_SEARCHES.map(saved => (
                      <SearchMenuItem
                        key={saved.label}
                        onClick={() => {
                          setSearchQuery(saved.query);
                          handleSearchSubmit(saved.route, saved.query);
                        }}
                      >
                        <span>{saved.label}</span>
                        <span className="hint">{saved.query}</span>
                      </SearchMenuItem>
                    ))}
                  </SearchMenuSection>
                </SearchMenu>
              )}
            </SearchBar>
          </TopBarLeft>

          <TopBarRight>
            {/* Mobile search trigger */}
            <IconBtn
              className="desktop-only"
              aria-label="Search"
              onClick={() => setIsMobileSearchOpen(true)}
              style={{ display: 'none' }}
            />
            <IconBtn
              aria-label="Search"
              onClick={() => setIsMobileSearchOpen(true)}
              style={{ display: 'none' }}
            />

            {/* Help — hidden on mobile */}
            <IconBtn className="desktop-only" aria-label="Help">
              <Icons.Help />
            </IconBtn>

            {/* Notifications */}
            <IconBtn
              aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
              onClick={() => setShowNotificationPanel(p => !p)}
            >
              <Icons.Bell />
              {unreadCount > 0 && <span className="dot">{unreadCount}</span>}
            </IconBtn>

            <Divider />

            {/* User pill — desktop */}
            <TopBarUserPill onClick={() => navigate('/profile')}>
              <MiniAvatar>{userInitials}</MiniAvatar>
              <MiniUserInfo>
                <div className="name">{user?.firstName}</div>
                <div className="online">● Online</div>
              </MiniUserInfo>
            </TopBarUserPill>

            {/* Mobile: search icon in top bar */}
            <IconBtn
              aria-label="Search"
              onClick={() => setIsMobileSearchOpen(true)}
              className="mobile-only"
            >
              <Icons.Search />
            </IconBtn>
          </TopBarRight>
        </TopBar>

        {/* Page Content */}
        <ContentArea>
          <SearchContext.Provider value={{ searchQuery, setSearchQuery }}>
            {children}
          </SearchContext.Provider>
        </ContentArea>
      </MainArea>

      {/* ── Bottom Navigation (Mobile) ────────────── */}
      <BottomNav aria-label="Bottom navigation">
        {bottomNavItems.map(item => (
          <BottomNavItem key={item.path} to={item.path} aria-label={item.label}>
            <span className="icon-wrap">
              <item.icon />
              {item.badge && <span className="badge">{item.badge}</span>}
            </span>
            <span className="label">{item.label}</span>
          </BottomNavItem>
        ))}

        {/* "More" triggers the bottom sheet */}
        <BottomNavMore onClick={() => setIsMoreSheetOpen(true)} aria-label="More options">
          <span className="icon-wrap"><Icons.More /></span>
          <span>More</span>
        </BottomNavMore>
      </BottomNav>

      {/* ── More Sheet Backdrop ───────────────────── */}
      <MoreSheetBackdrop
        $visible={isMoreSheetOpen}
        onClick={() => setIsMoreSheetOpen(false)}
        aria-hidden="true"
      />

      {/* ── More Bottom Sheet ─────────────────────── */}
      <MoreSheet $open={isMoreSheetOpen} role="dialog" aria-label="More options">
        <MoreSheetHandle />

        {/* Extra nav items that don't fit in bottom bar */}
        {mainNavItems.slice(4).map(item => (
          <MoreSheetItem key={item.path} onClick={() => { navigate(item.path); setIsMoreSheetOpen(false); }}>
            <div className="icon"><item.icon /></div>
            <div className="info">
              <div className="title">{item.label}</div>
            </div>
            <Icons.ChevronRight />
          </MoreSheetItem>
        ))}

        <MoreSheetItem onClick={() => { navigate('/profile'); setIsMoreSheetOpen(false); }}>
          <div className="icon"><Icons.Profile /></div>
          <div className="info">
            <div className="title">My Profile</div>
            <div className="desc">Edit your account details</div>
          </div>
          <Icons.ChevronRight />
        </MoreSheetItem>

        <MoreSheetItem onClick={() => { navigate('/change-password'); setIsMoreSheetOpen(false); }}>
          <div className="icon"><Icons.Lock /></div>
          <div className="info">
            <div className="title">Change Password</div>
            <div className="desc">Update your sign-in password</div>
          </div>
          <Icons.ChevronRight />
        </MoreSheetItem>

        <MoreSheetItem onClick={() => { setShowNotificationPanel(true); setIsMoreSheetOpen(false); }}>
          <div className="icon"><Icons.Bell /></div>
          <div className="info">
            <div className="title">Notifications</div>
            {unreadCount > 0 && <div className="desc">{unreadCount} unread</div>}
          </div>
          <Icons.ChevronRight />
        </MoreSheetItem>

        <MoreSheetItem onClick={() => {}}>
          <div className="icon"><Icons.Help /></div>
          <div className="info">
            <div className="title">Help & Support</div>
            <div className="desc">FAQs and contact</div>
          </div>
          <Icons.ChevronRight />
        </MoreSheetItem>

        <MoreSheetDivider />

        <MoreSheetDanger onClick={() => { setIsMoreSheetOpen(false); handleLogout(); }}>
          <div className="icon"><Icons.Logout /></div>
          <div className="info">
            <div className="title">Sign Out</div>
            <div className="desc">Log out of your account</div>
          </div>
        </MoreSheetDanger>
      </MoreSheet>

      {/* ── Mobile Search Overlay ─────────────────── */}
      <MobileSearchOverlay $open={isMobileSearchOpen} role="search">
        <MobileSearchBar>
          <Icons.Search />
          <MobileSearchInput
            ref={mobileSearchRef}
            type="search"
            placeholder="Search anything…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearchSubmit();
            }}
            aria-label="Search"
          />
          <MobileSearchCancel onClick={() => { setIsMobileSearchOpen(false); }}>
            Cancel
          </MobileSearchCancel>
        </MobileSearchBar>
        <MobileSearchHint>
          {searchQuery ? `Searching for "${searchQuery}"…` : 'Type to search across the app'}
        </MobileSearchHint>
      </MobileSearchOverlay>

      {/* ── Notification Panel ────────────────────── */}
      <NotificationPanel
        isOpen={showNotificationPanel}
        onClose={() => setShowNotificationPanel(false)}
        notifications={notifications}
        onRemoveNotification={handleRemoveNotification}
        onMarkNotificationAsRead={handleMarkNotificationAsRead}
        onClearNotifications={handleClearNotifications}
      />

      {/* ── License Modal ─────────────────────────── */}
      <LicensePermissionModal
        isOpen={showLicenseModal}
        onClose={() => setShowLicenseModal(false)}
        onSubmit={handleLicensePermissionSubmit}
        licenseExpiryDate={user?.licenseExpiry}
      />
    </LayoutRoot>
  );
};

export default Layout;
